// packages/web/src/components/teach/EditHomeworkModal.tsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Homework } from '../../services/homeworkApi';

interface EditHomeworkModalProps {
  homework: Homework;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: { name?: string; description?: string; due_date?: string }) => Promise<void>;
}

export const EditHomeworkModal: React.FC<EditHomeworkModalProps> = ({
  homework,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: homework.name,
    description: homework.description || '',
    due_date: homework.due_date.split('T')[0], // 转换为 YYYY-MM-DD 格式
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: homework.name,
        description: homework.description || '',
        due_date: homework.due_date.split('T')[0],
      });
      setError(null);
    }
  }, [isOpen, homework]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('作业名称不能为空');
      return;
    }

    if (!formData.due_date) {
      setError('截止日期不能为空');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        due_date: formData.due_date,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">编辑作业</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={saving}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* 作业名称 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              作业名称 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="请输入作业名称"
              maxLength={200}
              disabled={saving}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.name.length}/200
            </p>
          </div>

          {/* 作业描述 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              作业描述
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="请输入作业描述（可选）"
              rows={4}
              disabled={saving}
            />
          </div>

          {/* 截止日期 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              截止日期 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={saving}
            />
          </div>

          {/* 提示信息 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>注意：</strong>只能修改作业的基本信息（名称、描述、截止日期），不能修改习题内容和布置对象。
            </p>
          </div>

          {/* 按钮 */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={saving}
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
